import { useEffect, useMemo } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { z } from 'zod/v4';
import { useActiveRealmAPIContext } from '@interxfi/api-client';
import { useIxcCorpactionsCaadminV1ActionQueryServiceActionGet } from '@interxfi/api-client/generated/hooks';
import { useIxcCorpactionsCaadminV1ManualOverrideCommandServiceManualDefault } from '@interxfi/api-client/generated/hooks/ixc-corpactions-caadmin-v1-manual-override-command-service-manual-default';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { useIsWriteAllowed } from '@interxfi/design-system/access-context';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { Hint } from '@interxfi/design-system/components/hint/hint';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import { TextButton } from '@interxfi/design-system/components/link/text-link/text-button';
import { baseTextLinkTypographyBySize, TextLink } from '@interxfi/design-system/components/link/text-link/text-link';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Pill } from '@interxfi/design-system/components/pill/pill';
import { StatusBar } from '@interxfi/design-system/components/status-bar/status-bar';
import { Typography, type TypographyProps } from '@interxfi/design-system/components/typography';
import AlertIcon from '@interxfi/design-system/icons/react/icon-alert';
import {
  type IxcCorpactionsCaadminV1ActionState,
  IxcCorpactionsCaadminV1ActionStatus,
  type IxcCorpactionsCaadminV1ManualOverride,
} from '@interxfi/types';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { CorpactActionCustomerCashDeltasTable } from '../../../../../../../../../../features/corporate-action/action/corpact-action-customer-cash-deltas-table';
import { CorpactActionCustomerSecurityDeltasTable } from '../../../../../../../../../../features/corporate-action/action/corpact-action-customer-security-deltas-table';
import { getCorpactCustomerActionListItems } from '../../../../../../../../../../features/corporate-action/action/corpact-action-list-items';
import { CorpactActionTypePill } from '../../../../../../../../../../features/corporate-action/action/corpact-action-type-pill';
import { CorpactCaAdminActionBuildingCustomerCashDeltasTable } from '../../../../../../../../../../features/corporate-action/ca-admin/action/ca-admin-action-building-customer-cash-deltas-table';
import { CorpactCaAdminActionBuildingCustomerSecurityDeltasTable } from '../../../../../../../../../../features/corporate-action/ca-admin/action/ca-admin-action-building-customer-security-deltas-table';
import { CorpactCaAdminDtcCancellationDetailDrawer } from '../../../../../../../../../../features/corporate-action/ca-admin/action/ca-admin-action-dtc-cancellation-detail-drawer';
import { CorpactCaAdminActionEntityMenu } from '../../../../../../../../../../features/corporate-action/ca-admin/action/ca-admin-action-entity-menu';
import { CorpactCaAdminActionEventSummary } from '../../../../../../../../../../features/corporate-action/ca-admin/action/ca-admin-action-event';
import {
  type CaAdminCorporateActionVersionData,
  getCustomerActionForVersionId,
} from '../../../../../../../../../../features/corporate-action/ca-admin/action/ca-admin-action-helpers';
import {
  getCorpactCaAdminActionStateListItems,
  getCorpactCaAdminBuildingCustomerActionListItems,
} from '../../../../../../../../../../features/corporate-action/ca-admin/action/ca-admin-action-list-items';
import { getCorpactCaAdminActionDetailPath } from '../../../../../../../../../../features/corporate-action/ca-admin/action/ca-admin-action-routing';
import { CorpactCaAdminActionStatusPill } from '../../../../../../../../../../features/corporate-action/ca-admin/action/ca-admin-action-status-pill';
import { CorpactCaAdminActionSummaryBar } from '../../../../../../../../../../features/corporate-action/ca-admin/action/ca-admin-action-summary-bar';
import { CorpactCaAdminApproveCandidateVersionConfirmDialog } from '../../../../../../../../../../features/corporate-action/ca-admin/action/ca-admin-approve-candidate-version-confirm-dialog';
import { CorpactCaAdminConversionErrorsDrawer } from '../../../../../../../../../../features/corporate-action/ca-admin/ca-admin-conversion-errors-drawer';
import { CorpactCaAdminValidationErrorsDrawer } from '../../../../../../../../../../features/corporate-action/ca-admin/ca-admin-validation-errors-drawer';
import { CorpactOverrideIndicator } from '../../../../../../../../../../features/corporate-action/override-indicator/corpact-override-indicator';
import { DtccCaCashMovementsTable } from '../../../../../../../../../../features/dtcc/ca/cash-movements/dtcc-ca-cash-movements-table';
import { DtccCaSecurityMovementsTable } from '../../../../../../../../../../features/dtcc/ca/security-movements/dtcc-ca-security-movements-table';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

const searchSchema = z.object({
  versionId: z.string().optional(),
});

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/operations/_operations/ca/action/_action/$actionId')({
  validateSearch: searchSchema,
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `corporate-action-${params.actionId}`,
        label: 'Action',
        to: getCorpactCaAdminActionDetailPath(params.actionId),
        suffix: <CopyButton content={params.actionId} label="Copy Action ID" tooltip={{ copy: params.actionId, copied: 'Copied Action ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Corporate Action'),
  component: CorporateActionDetail,
});

const statusBannerCtaTypography = {
  ...baseTextLinkTypographyBySize.small,
  tone: 'background',
  inheritance: 'current',
} as const satisfies TypographyProps;

interface CorpactActionViewContext {
  actionId: string;
  approvedVersionId: string | undefined;
  candidateVersionId: string | undefined;
  renderedVersionId: string | undefined;
  candidateMatchesApprovedVersion: boolean;
  isRenderingCandidateVersion: boolean;
  isRenderingApprovedVersion: boolean;
  isRenderingHistoricalVersion: boolean;
  versionData: CaAdminCorporateActionVersionData | undefined;
}

function checkIfActionContainsVersionId(action: IxcCorpactionsCaadminV1ActionState | undefined, versionId: string | undefined) {
  if (!action || !versionId) {
    return false;
  }

  return [
    ...(action.data.approvedVersion?.versionId ? [action.data.approvedVersion.versionId] : []),
    ...(action.data.candidateVersion?.versionId ? [action.data.candidateVersion.versionId] : []),
    ...(action.data.referencedVersions?.map((version) => version.versionId) ?? []),
  ].includes(versionId);
}

function getViewContext(
  actionId: string,
  action: IxcCorpactionsCaadminV1ActionState | undefined,
  versionId: string | undefined,
): CorpactActionViewContext {
  const approvedVersionId = action?.data?.approvedVersion?.versionId;
  const candidateVersionId = action?.data?.candidateVersion?.versionId;
  const renderedVersionId = checkIfActionContainsVersionId(action, versionId) ? versionId : (approvedVersionId ?? candidateVersionId);

  return {
    actionId,
    approvedVersionId,
    candidateVersionId,
    renderedVersionId,
    candidateMatchesApprovedVersion: Boolean(candidateVersionId && approvedVersionId && candidateVersionId === approvedVersionId),
    isRenderingCandidateVersion: Boolean(candidateVersionId && renderedVersionId === candidateVersionId && renderedVersionId !== approvedVersionId),
    isRenderingApprovedVersion: Boolean(approvedVersionId && renderedVersionId === approvedVersionId),
    isRenderingHistoricalVersion: Boolean(renderedVersionId && renderedVersionId !== approvedVersionId && renderedVersionId !== candidateVersionId),
    versionData: getCustomerActionForVersionId(action, renderedVersionId),
  };
}

function getListItems(
  data: IxcCorpactionsCaadminV1ActionState | undefined,
  // Null here means pending
  externalValuesAsOverrides: IxcCorpactionsCaadminV1ManualOverride | undefined | null,
  viewContext: CorpactActionViewContext,
) {
  const items = getCorpactCaAdminActionStateListItems(data);
  const renderedVersionItems = viewContext.versionData
    ? viewContext.versionData.kind === 'full'
      ? getCorpactCustomerActionListItems(viewContext.versionData.value, 'renderedVersionData.')
      : getCorpactCaAdminBuildingCustomerActionListItems(viewContext.versionData.value, 'renderedVersionData.')
    : undefined;

  // const canShowOverrides = externalValuesAsOverrides !== null && viewContext.isRenderingCandidateVersion;

  return {
    general: {
      left: [
        { ...(items.data.underlyingSecurity.summary ?? items.data.underlyingSecurity.assetId), title: 'Underlying Security' },
        ...(viewContext.isRenderingApprovedVersion && items.data.approvedVersion
          ? [items.data.approvedVersion.timestamp, items.data.approvedVersion.triggered, items.data.approvedVersion.triggerTimestamp]
          : []),
        ...(viewContext.isRenderingCandidateVersion && items.data.candidateVersion
          ? [items.data.candidateVersion.draft, items.data.candidateVersion.canceled]
          : []),
        ...(renderedVersionItems?.header
          ? [
              ...(renderedVersionItems.header.actionTypePill ? [renderedVersionItems.header.actionTypePill] : []),
              ...(renderedVersionItems.header.actionTypeLabel ? [renderedVersionItems.header.actionTypeLabel] : []),
              ...(renderedVersionItems.header.actionTypeSubLabel ? [renderedVersionItems.header.actionTypeSubLabel] : []),
              ...(renderedVersionItems.header.paymentType ? [renderedVersionItems.header.paymentType] : []),
              ...(renderedVersionItems.header.description ? [renderedVersionItems.header.description] : []),
              ...(renderedVersionItems.header.taxYear ? [renderedVersionItems.header.taxYear] : []),
            ]
          : []),
        // ...(renderedVersionItems?.header
        //   ? [
        //       getWrappedCorpactOverrideListItem(
        //         renderedVersionItems.header.actionTypePill,
        //         externalValuesAsOverrides?.header?.actionType ?? EMPTY_VALUE,
        //         Boolean(
        //           canShowOverrides &&
        //             data?.data?.manualOverride?.header?.actionType &&
        //             data.data.manualOverride.header.actionType !== externalValuesAsOverrides?.header?.actionType,
        //         ),
        //       ),
      ] as const satisfies ReadonlyListItem[],
      right: [
        items.actionId,
        items.assetId,
        ...(viewContext.isRenderingApprovedVersion && items.data.approvedVersion ? [items.data.approvedVersion.versionId] : []),
        ...(viewContext.isRenderingCandidateVersion && items.data.candidateVersion ? [items.data.candidateVersion.versionId] : []),
        ...(renderedVersionItems?.header
          ? [
              ...(renderedVersionItems.header.cusip ? [renderedVersionItems.header.cusip] : []),
              ...(renderedVersionItems.header.recordDate ? [renderedVersionItems.header.recordDate] : []),
              ...(renderedVersionItems.header.exDate ? [renderedVersionItems.header.exDate] : []),
              ...(renderedVersionItems.header.paymentDate ? [renderedVersionItems.header.paymentDate] : []),
              ...(renderedVersionItems.header.cumDate ? [renderedVersionItems.header.cumDate] : []),
              ...(renderedVersionItems.header.effectiveDate ? [renderedVersionItems.header.effectiveDate] : []),
              ...(renderedVersionItems.header.timeApplicable ? [renderedVersionItems.header.timeApplicable] : []),
            ]
          : []),
      ] as const satisfies ReadonlyListItem[],
    },
    dtcNotification:
      data?.data?.dtc?.notification && items.data.dtc?.notification
        ? ([
            {
              id: 'dtcNotificationMain',
              items: [
                ...(data.data.dtc.notification.ticker ? [items.data.dtc.notification.ticker] : []),
                ...(data.data.dtc.notification.dtcAssetClass ? [items.data.dtc.notification.dtcAssetClass] : []),
                ...(data.data.dtc.notification.dtcAssetType ? [items.data.dtc.notification.dtcAssetType] : []),
                ...(data.data.dtc.notification.offerType ? [items.data.dtc.notification.offerType] : []),
                ...(data.data.dtc.notification.dividendType ? [items.data.dtc.notification.dividendType] : []),
                items.data.dtc.notification.timestamp,
                ...(data.data.dtc.notification.dtcCaptureDate ? [items.data.dtc.notification.dtcCaptureDate] : []),
                ...(data.data.dtc.notification.recordDate ? [items.data.dtc.notification.recordDate] : []),
                ...(data.data.dtc.notification.exDividendDate ? [items.data.dtc.notification.exDividendDate] : []),
                ...(data.data.dtc.notification.effectiveDate ? [items.data.dtc.notification.effectiveDate] : []),
                ...(data.data.dtc.notification.interestAccruedDays ? [items.data.dtc.notification.interestAccruedDays] : []),
                ...(data.data.dtc.notification.additionalInfo ? [items.data.dtc.notification.additionalInfo] : []),
              ],
            },
            ...(data.data.dtc.notification.header
              ? [
                  {
                    id: 'dtcNotificationHeader',
                    title: 'Header',
                    items: [
                      items.data.dtc.notification.header.assetId,
                      items.data.dtc.notification.header.cusip,
                      items.data.dtc.notification.header.eventId,
                      ...(data.data.dtc.notification.header.officialEventId ? [items.data.dtc.notification.header.officialEventId] : []),
                      ...(data.data.dtc.notification.header.modifiedTimestamp ? [items.data.dtc.notification.header.modifiedTimestamp] : []),
                      items.data.dtc.notification.header.eventType,
                      items.data.dtc.notification.header.subEventType,
                      items.data.dtc.notification.header.processingType,
                      { ...items.data.dtc.notification.header.processingStatus.eventCompletenessStatus, title: 'Completeness Status' },
                      { ...items.data.dtc.notification.header.processingStatus.eventConfirmationStatus, title: 'Confirmation Status' },
                    ],
                  },
                ]
              : []),
          ] as const satisfies ReadonlyListGroup[])
        : undefined,
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load corporate action', 'view this corporate action');

function CorporateActionDetail() {
  const { actionId } = Route.useParams();
  const { versionId } = Route.useSearch();
  const isWriteAllowed = useIsWriteAllowed();
  const request = { actionId } as const;
  const { data, error, isPending } = useIxcCorpactionsCaadminV1ActionQueryServiceActionGet(request);
  useErrorHandler(error, errorMessageGetter);
  const { token } = useActiveRealmAPIContext();
  const {
    data: manualDefaultResponse,
    mutate: getManualDefault,
    isPending: manualDefaultIsPending,
  } = useIxcCorpactionsCaadminV1ManualOverrideCommandServiceManualDefault();
  const showPending = Boolean(isPending || error || manualDefaultIsPending);
  const viewContext = useMemo(() => getViewContext(actionId, data?.action, versionId), [actionId, data?.action, versionId]);
  const listItems = useMemo(
    () => getListItems(data?.action, manualDefaultResponse ? manualDefaultResponse?.spec : manualDefaultIsPending ? null : undefined, viewContext),
    [data?.action, manualDefaultResponse, manualDefaultIsPending, viewContext],
  );
  const {
    approvedVersionId,
    candidateVersionId,
    candidateMatchesApprovedVersion,
    renderedVersionId,
    isRenderingCandidateVersion,
    isRenderingHistoricalVersion,
    versionData,
  } = viewContext;
  const basePath = getCorpactCaAdminActionDetailPath(actionId);
  const approvedVersionPath = approvedVersionId ? getCorpactCaAdminActionDetailPath(actionId, approvedVersionId) : undefined;

  useEffect(() => {
    // Ensure authentication (useMutation doesn't have the "enabled" gate like useQuery does, so this is manual here)
    if (token) {
      getManualDefault({ actionId });
    }
  }, [getManualDefault, actionId, token]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          {isRenderingCandidateVersion && !candidateMatchesApprovedVersion ? (
            <Hint blockAlign="center" size="small" variant="important" withBorderRadius>
              You are viewing a candidate version of this action.
              {renderedVersionId &&
              isWriteAllowed &&
              (data?.action?.status === IxcCorpactionsCaadminV1ActionStatus.Draft ||
                data?.action?.status === IxcCorpactionsCaadminV1ActionStatus.UpdateDraft) ? (
                <span>
                  {' '}
                  <CorpactCaAdminApproveCandidateVersionConfirmDialog
                    actionId={actionId}
                    trigger={
                      <TextButton size="small" typography={statusBannerCtaTypography} underline="always">
                        Approve the candidate version
                      </TextButton>
                    }
                  />{' '}
                  so it can be triggered.
                </span>
              ) : (
                ' It must be approved before it can be triggered'
              )}
              {approvedVersionPath ? (
                <>
                  {' '}
                  <TextLink LinkElement={Link} size="small" to={approvedVersionPath} typography={statusBannerCtaTypography} underline="always">
                    View active version
                  </TextLink>
                </>
              ) : null}
            </Hint>
          ) : candidateVersionId && !candidateMatchesApprovedVersion && basePath ? (
            <Hint blockAlign="center" size="small" variant="important" withBorderRadius>
              This action has a candidate version.{' '}
              <TextLink LinkElement={Link} size="small" to={basePath} typography={statusBannerCtaTypography} underline="always">
                View candidate version
              </TextLink>
            </Hint>
          ) : null}

          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              {showPending || versionData?.value.header?.actionType ? (
                <CorpactActionTypePill actionType={versionData?.value.header?.actionType} isPending={showPending} size="large" />
              ) : null}
              <CorpactCaAdminActionStatusPill isPending={showPending} size="large" status={data?.action?.status} />

              {isRenderingHistoricalVersion ? <Pill variant="dark">Historical</Pill> : null}

              {data?.action?.data?.manualOverride ? (
                <Pill size="large" variant="danger">
                  Manual Override
                </Pill>
              ) : null}
            </HorizontalStack>

            <CorpactCaAdminActionEntityMenu actionId={actionId} selectedVersionId={renderedVersionId} />
          </PageHeader>

          <CorpactCaAdminActionSummaryBar {...request} />

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <CorpactCaAdminActionEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={10}>
          <VerticalStack gap={7}>
            <HorizontalStack align="space-between" blockAlign="center" gap={2} wrap="nowrap">
              <Typography.Level3>Data</Typography.Level3>
              {data?.action?.data?.manualOverride ? <CorpactOverrideIndicator label="Signifies Manual Override" /> : null}
            </HorizontalStack>

            {isRenderingCandidateVersion && data?.action?.data?.candidateVersion?.conversionErrors?.length ? (
              <StatusBar
                actions={[
                  {
                    id: 'viewConversionErrors',
                    triggerStyle: 'button',
                    ctaText: 'View Conversion Errors',
                    type: 'drawer',
                    drawer: {
                      Component: CorpactCaAdminConversionErrorsDrawer,
                      props: { errors: data.action.data.candidateVersion.conversionErrors },
                    },
                  },
                ]}
                icon={<AlertIcon aria-hidden width={16} />}
                title="Conversion Errors must be resolved before this version can be approved and triggered"
                variant="danger"
              />
            ) : null}

            {isRenderingCandidateVersion && data?.action?.data?.candidateVersion?.validationErrors?.length ? (
              <StatusBar
                actions={[
                  {
                    id: 'viewValidationErrors',
                    triggerStyle: 'button',
                    ctaText: 'View Validation Errors',
                    type: 'drawer',
                    drawer: {
                      Component: CorpactCaAdminValidationErrorsDrawer,
                      props: { errors: data.action.data.candidateVersion.validationErrors },
                    },
                  },
                ]}
                icon={<AlertIcon aria-hidden width={16} />}
                title="Validation Errors must be resolved before this version can be approved and triggered"
                variant="danger"
              />
            ) : null}

            <WrappingLayoutContainer
              gap={DEFAULT_PAGE_LAYOUT_GAP}
              left={{
                ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
                render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
              }}
              padding={LAYOUT_ZERO_CONTAINER_PADDING}
              right={{
                ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
                render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
              }}
              wrap="forward"
              wrapGap={7}
            />
          </VerticalStack>

          <VerticalStack gap={7}>
            <Typography.Level3>Cash Deltas</Typography.Level3>

            {versionData?.kind === 'partial' ? (
              <CorpactCaAdminActionBuildingCustomerCashDeltasTable deltas={versionData.value.cashDeltas ?? STABLE_EMPTY_ARRAY_REF} loadingRows={1} />
            ) : (
              <CorpactActionCustomerCashDeltasTable deltas={versionData?.value?.cashDeltas ?? STABLE_EMPTY_ARRAY_REF} loadingRows={1} />
            )}
          </VerticalStack>

          <VerticalStack gap={7}>
            <Typography.Level3>Security Deltas</Typography.Level3>

            {versionData?.kind === 'partial' ? (
              <CorpactCaAdminActionBuildingCustomerSecurityDeltasTable
                deltas={versionData.value.securityDeltas ?? STABLE_EMPTY_ARRAY_REF}
                loadingRows={1}
              />
            ) : (
              <CorpactActionCustomerSecurityDeltasTable deltas={versionData?.value.securityDeltas ?? STABLE_EMPTY_ARRAY_REF} loadingRows={1} />
            )}
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      {/* As more data sources are added, e.g., ICE, this will become a tabbed section (by vendor) */}
      {data?.action?.data?.dtc ? (
        <PageLayout.Section>
          <VerticalStack gap={10}>
            <VerticalStack gap={7}>
              <Typography.Level3>DTC Data</Typography.Level3>

              {data.action.data.dtc.cancellation ? (
                <StatusBar
                  actions={[
                    {
                      id: 'viewDtcCancellation',
                      triggerStyle: 'button',
                      variant: 'primary',
                      type: 'modal',
                      modal: { Component: CorpactCaAdminDtcCancellationDetailDrawer, props: { cancellation: data.action.data.dtc.cancellation } },
                    },
                  ]}
                  title="DTC cancellation notice received for this action"
                  variant="text"
                />
              ) : null}

              {listItems.dtcNotification ? <AppReadonlyListStack groups={listItems.dtcNotification} titleGap={4} /> : null}
            </VerticalStack>

            {data.action.data.dtc.notification ? (
              <>
                <VerticalStack gap={7}>
                  <Typography.Level3>Cash Movements</Typography.Level3>
                  <DtccCaCashMovementsTable
                    cashMovements={data.action.data.dtc.notification.cashMovements ?? STABLE_EMPTY_ARRAY_REF}
                    loadingRows={1}
                  />
                </VerticalStack>

                <VerticalStack gap={7}>
                  <Typography.Level3>Security Movements</Typography.Level3>
                  <DtccCaSecurityMovementsTable
                    loadingRows={1}
                    securityMovements={data.action.data.dtc.notification.securityMovements ?? STABLE_EMPTY_ARRAY_REF}
                  />
                </VerticalStack>
              </>
            ) : null}
          </VerticalStack>
        </PageLayout.Section>
      ) : null}
    </PageLayout>
  );
}
